import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { getVisitors, createVisitor } from '@/services/api';
import { Plus, UserCheck, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const ResidentVisitors = () => {
  const { user } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    purpose: ''
  });

  useEffect(() => {
    fetchVisitors();
  }, []);

  const fetchVisitors = async () => {
    try {
      const data = await getVisitors();
      const myVisitors = data.filter(v => v.host_user_id === user?.id);
      setVisitors(myVisitors);
    } catch (error) {
      toast.error('Failed to load visitors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createVisitor({
        ...formData,
        host_user_id: user.id,
        host_name: user.name,
        host_unit: user.unit_number || 'N/A'
      });
      toast.success('Visitor registered successfully');
      setDialogOpen(false);
      setFormData({ name: '', phone: '', purpose: '' });
      fetchVisitors();
    } catch (error) {
      toast.error('Failed to register visitor');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, variant: 'secondary', label: 'Pending' },
      approved: { icon: CheckCircle2, variant: 'default', label: 'Approved' },
      checked_in: { icon: UserCheck, variant: 'default', label: 'Checked In' },
      checked_out: { icon: CheckCircle2, variant: 'outline', label: 'Checked Out' },
      rejected: { icon: XCircle, variant: 'destructive', label: 'Rejected' }
    };
    const item = config[status] || config.pending;
    const Icon = item.icon;
    return (
      <Badge variant={item.variant}>
        <Icon className="h-3 w-3 mr-1" />
        {item.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="resident-visitors">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Visitors</h1>
          <p className="text-slate-600">Manage your visitor passes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-secondary hover:bg-secondary/90" data-testid="new-visitor-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Visitor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Register New Visitor</DialogTitle>
              <DialogDescription>Pre-approve a visitor for quick entry</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Visitor Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="visitor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  placeholder="+1 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="visitor-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purpose">Purpose of Visit</Label>
                <Textarea
                  id="purpose"
                  placeholder="e.g., Personal visit, Delivery, etc."
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  required
                  data-testid="visitor-purpose-input"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90" data-testid="visitor-submit-btn">Register Visitor</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {visitors.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No visitors registered yet</p>
            </CardContent>
          </Card>
        ) : (
          visitors.map((visitor) => (
            <Card key={visitor.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="visitor-item">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">{visitor.name}</h3>
                    <p className="text-sm text-slate-600">{visitor.phone}</p>
                  </div>
                  {getStatusBadge(visitor.status)}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Purpose</p>
                    <p className="text-sm font-medium text-slate-900">{visitor.purpose}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Gate Pass Code</p>
                    <p className="text-lg font-bold text-secondary font-mono">{visitor.gate_pass_code}</p>
                  </div>
                </div>
                {visitor.entry_time && (
                  <div className="text-xs text-slate-500">
                    Entry: {new Date(visitor.entry_time).toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ResidentVisitors;